import { useCallback, useContext, useEffect, useRef } from "react";
import { LoginUserContext } from "../../../providers/LoginUserProvider";
import { getAuth } from "firebase/auth";
import { useNotification } from "./NotificationProvider";
import { useMarkAsReadNotifications } from "./hooks";

const MAX_RETRY_COUNT = 5;
const MARK_AS_READ_ALL_NOTIFICATIONS = 9;
export const useConnectNotifications = () => {
  const currentUser = useContext(LoginUserContext);
  const { uid } = currentUser?.loginUser ?? {};
  const getToken = useCallback(async () => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    return token || "";
  }, []);
  const {
    state: { actionAnchorElement, hasNotificationUnread },
    addNotification,
    updateHasNotificationUnread,
  } = useNotification();
  const { markAsReadNotifications } = useMarkAsReadNotifications();
  const lastMessageTimestamp = useRef<number>(0);
  const retryCount = useRef(0);
  useEffect(() => {
    const wsEndpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT;
    if (!wsEndpoint || wsEndpoint.includes("localhost")) {
      return;
    }

    let wsInstance: WebSocket | null = null;
    let heartbeat: NodeJS.Timeout;
    let isMounted = true;
    const connect = async () => {
      try {
        const token = await getToken();
        const newWs = new WebSocket(
          `${wsEndpoint}/ws/notifications/${uid}?token=${token}`
        );

        newWs.onopen = () => {
          if (!isMounted) return;
          retryCount.current = 0;
          heartbeat = setInterval(() => {
            if (newWs.readyState === WebSocket.OPEN) {
              newWs.send(JSON.stringify({ type: "ping" }));
            }
          }, 5000);
        };

        newWs.onmessage = async (event) => {
          try {
            const newMessage = event?.data ?? "";
            const parseMessage = JSON.parse(newMessage) ?? {};
            const parseType = parseMessage?.data?.type;
            if (parseType === "ping") {
              return;
            }
            const currentMessageTimestamp = Date.now();
            if (parseType === MARK_AS_READ_ALL_NOTIFICATIONS) {
              lastMessageTimestamp.current = currentMessageTimestamp;
              if (hasNotificationUnread) updateHasNotificationUnread(false);
              return;
            }

            const parseMessageData = parseMessage?.data;
            if (parseMessageData) {
              let isRead = parseMessageData?.is_read;
              if (Boolean(actionAnchorElement)) {
                if (!isRead) {
                  const res = (await markAsReadNotifications()) as any;
                  if (res?.isSuccess) {
                    isRead = true;
                  }
                }
              }

              if (!isRead) {
                setTimeout(() => {
                  if (currentMessageTimestamp > lastMessageTimestamp.current) {
                    updateHasNotificationUnread(true);
                  }
                }, 1000);
              }
              addNotification({ ...parseMessageData, is_read: isRead });
            }
          } catch (error) {
            console.error(error);
          }
        };

        newWs.onclose = () => {
          if (!isMounted) return;
          clearInterval(heartbeat);
          if (retryCount.current < MAX_RETRY_COUNT) {
            retryCount.current++;
            setTimeout(connect, 5000);
          }
        };

        newWs.onerror = () => {
          // エラーはoncloseで処理するため、ここでは何もしない
        };

        if (isMounted) {
          wsInstance = newWs;
        }
      } catch (error) {
        if (retryCount.current < MAX_RETRY_COUNT) {
          setTimeout(connect, 5000);
        }
        retryCount.current++;
      }
    };

    connect();

    return () => {
      isMounted = false;
      clearInterval(heartbeat);
      if (wsInstance) {
        wsInstance.close();
      }
    };
  }, [actionAnchorElement, hasNotificationUnread, getToken, uid]);
};
